import React, { Component } from "react";
import QrReader from "react-qr-reader-es6";
import { Button, Typography, Snackbar, Alert, TextField, Dialog, DialogActions, DialogContent, DialogTitle } from "@mui/material";

class QrScan extends Component {
  constructor(props) {
    super(props);
    this.state = {
      result: "",
      open: false,
      description: "",
      dialogOpen: false,
      descriptionError: false,
      error: "", // Error state for displaying alerts
      success: false, // Success state for displaying success Snackbar
      alreadyDamaged: false, // State to track if the equipment is already damaged
    };
  }

  handleScan = (data) => {
    if (data) {
      this.setState({
        result: data,
      });
    }
  };

  handleError = (err) => {
    console.error(err);
  };

  handleClick = () => {
    this.setState({ dialogOpen: true });
  };

  handleClose = (event, reason) => {
    if (reason === "clickaway") {
      return;
    }
    this.setState({ open: false, description: "", error: "", success: false, alreadyDamaged: false }); // Reset states after Snackbar closes
  };

  handleDialogClose = () => {
    this.setState({ dialogOpen: false, descriptionError: false });
  };

  handleDescriptionChange = (event) => {
    this.setState({ description: event.target.value, descriptionError: false });
  };

  // Function to update the equipment status in the database to "damaged"
  updateEquipmentStatus = async (equipmentId, description) => {
    try {
      // First, check if the equipment is already damaged
      const getResponse = await fetch(
        `https://k0uhot18c2.execute-api.us-east-1.amazonaws.com/staging/equipments/${equipmentId}`,
        {
          method: "GET",
          headers: {
            "Content-Type": "application/json",
          },
        }
      );

      if (!getResponse.ok) {
        throw new Error("Failed to fetch equipment data");
      }

      const equipmentData = await getResponse.json();

      // Check if the equipment is already marked as damaged
      if (equipmentData[0]?.Damaged === true) {  // Make sure to check `Damaged` (capital D)
        this.setState({ alreadyDamaged: true, open: true });
        return; // Stop further execution if the item is already damaged
      }

      // Update the equipment status to damaged and include damageDescription
      const updateResponse = await fetch(
        `https://k0uhot18c2.execute-api.us-east-1.amazonaws.com/staging/equipments/${equipmentId}`,
        {
          method: "PUT",
          headers: {
            "Content-Type": "application/json",
          },
          body: JSON.stringify({
            Damaged: true,  // Ensure the field is named Damaged (capital D)
            damageDescription: description, // Mark the equipment as damaged and include description
          }),
        }
      );

      if (!updateResponse.ok) {
        throw new Error("Failed to update equipment status");
      }

      console.log("Equipment status updated to damaged");

      // Send notification (SNS trigger) after marking the equipment as damaged
      await this.sendNotification(equipmentId, description);

      // Set success state to show green Snackbar
      this.setState({ success: true, open: true });
    } catch (error) {
      console.error("Error updating equipment status:", error);
      this.setState({ error: "Error sending report", open: true });
    }
  };

  // Function to send the SNS notification after equipment is marked as damaged
  sendNotification = async (equipmentId, description) => {
    const body = JSON.stringify({ equipmentId, description });

    try {
      const response = await fetch(
        "https://hosxvcbryh.execute-api.us-east-1.amazonaws.com/Stage/snsApi",
        {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
          },
          body: body,
        }
      );

      if (!response.ok) {
        throw new Error("Failed to send SNS report");
      }

      const data = await response.json();
      console.log("Response from SNS API:", data);
    } catch (error) {
      console.error("Error sending SNS notification:", error);
    }
  };

  handleReportSubmit = () => {
    if (!this.state.description) {
      this.setState({ descriptionError: true });
      return;
    }

    // Call the updateEquipmentStatus to mark it as damaged and send the notification
    this.updateEquipmentStatus(this.state.result, this.state.description);

    this.setState({
      dialogOpen: false,
      descriptionError: false, // Reset error after successful submission
    });
  };

  render() {
    return (
      <div align="center">
        <Typography
          align="center"
          variant="h6"
          component="div"
          sx={{ flexGrow: 1, m: 2 }}
        >
          Report Damaged Equipment
        </Typography>

        <QrReader
          delay={1000}
          onError={this.handleError}
          onScan={this.handleScan}
          style={{ width: "70%" }}
          constraints={{
            facingMode: "environment", // Use the back camera
          }}
        />

        <p>Equipment ID: {this.state.result}</p>

        <Button
          variant="contained"
          onClick={this.handleClick}
          disabled={!this.state.result} // Disable button if no QR code is scanned
        >
          Report Damaged Equipment
        </Button>

        {/* Dialog to provide damage description */}
        <Dialog open={this.state.dialogOpen} onClose={this.handleDialogClose}>
          <DialogTitle>Provide damage description</DialogTitle>
          <DialogContent>
            <TextField
              autoFocus
              margin="dense"
              label="Damage Description"
              fullWidth
              value={this.state.description}
              onChange={this.handleDescriptionChange}
              error={this.state.descriptionError} // Highlight field in case of error
              helperText={
                this.state.descriptionError
                  ? "Damage description cannot be empty."
                  : ""
              }
            />
          </DialogContent>
          <DialogActions>
            <Button onClick={this.handleDialogClose}>Cancel</Button>
            <Button onClick={this.handleReportSubmit} variant="contained">
              Submit
            </Button>
          </DialogActions>
        </Dialog>

        {/* Conditional Snackbar for success or failure */}
        {this.state.success ? (
          <Snackbar
            open={this.state.open}
            autoHideDuration={5000}
            onClose={this.handleClose}
          >
            <Alert onClose={this.handleClose} severity="success" sx={{ width: "100%" }}>
              Damaged Equipment Reported: {this.state.result} - {this.state.description}
            </Alert>
          </Snackbar>
        ) : this.state.alreadyDamaged ? (
          <Snackbar
            open={this.state.open}
            autoHideDuration={5000}
            onClose={this.handleClose}
          >
            <Alert onClose={this.handleClose} severity="error" sx={{ width: "100%" }}>
              Equipment {this.state.result} is already marked as damaged.
            </Alert>
          </Snackbar>
        ) : (
          <Snackbar
            open={this.state.open}
            autoHideDuration={5000}
            onClose={this.handleClose}
          >
            <Alert onClose={this.handleClose} severity="error" sx={{ width: "100%" }}>
              {this.state.error}
            </Alert>
          </Snackbar>
        )}
      </div>
    );
  }
}

export default QrScan;
